import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download } from 'lucide-react';

const COLUNAS_TEMPLATE = [
  { col: 'A', nome: 'Nome do produto', obrig: true },
  { col: 'B', nome: 'Código do produto (SKU)', obrig: false },
  { col: 'C', nome: 'Preço de venda', obrig: false },
  { col: 'D', nome: 'Código de barras (GTIN/EAN)', obrig: false },
  { col: 'E', nome: 'Unidade de medida', obrig: false },
  { col: 'F', nome: 'NCM', obrig: false },
  { col: 'G', nome: 'Categoria do produto', obrig: false },
  { col: 'H', nome: 'Peso bruto (Kg)', obrig: false },
  { col: 'I', nome: 'Peso líquido (Kg)', obrig: false },
  { col: 'J', nome: 'Estoque máximo', obrig: false },
  { col: 'K', nome: 'Estoque mínimo', obrig: false },
  { col: 'L', nome: 'Origem do produto', obrig: false },
  { col: 'M', nome: 'CEST', obrig: false },
];

export default function ImportarProdutos() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError('Apenas arquivos .xlsx ou .xls são aceitos');
      return;
    }
    setFile(f);
    setError('');
    setResultado(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) { setError('Selecione um arquivo primeiro'); return; }
    setLoading(true);
    setError('');
    setResultado(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/produtos/importar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultado(res.data);
    } catch (err) {
      setError(err.userMessage || err.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Gera CSV simples como template
    const header = COLUNAS_TEMPLATE.map(c => c.nome).join(';');
    const example = 'Pão Francês;PAO001;2.50;;Unidade;1905.90.90;Pães;0.100;0.080;500;100;0;';
    const csv = `${header}\n${example}`;
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_produtos.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="importar-produtos">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/produtos')} className="p-2 rounded-lg hover:bg-brand-bg">
          <ArrowLeft className="w-5 h-5 text-brand-text-muted" />
        </button>
        <div>
          <h1 className="dashboard-heading text-2xl">Importar Produtos (XLSX)</h1>
          <p className="text-brand-text-muted font-body text-sm">Importe em massa via planilha Excel</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Upload area */}
        <div className="md:col-span-2 space-y-4">
          {/* Drop zone */}
          <div
            className={`card-dashboard border-2 border-dashed transition-colors cursor-pointer ${dragging ? 'border-brand-primary bg-brand-accent-light/30' : 'border-brand-border hover:border-brand-accent-mid'}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={e => handleFile(e.target.files[0])}
              data-testid="file-input"
            />
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <FileSpreadsheet className={`w-12 h-12 ${file ? 'text-brand-primary' : 'text-brand-text-muted'}`} />
              {file ? (
                <>
                  <p className="font-dashboard font-semibold text-brand-text">{file.name}</p>
                  <p className="text-xs text-brand-text-muted font-body">{(file.size / 1024).toFixed(1)} KB · Clique para trocar</p>
                </>
              ) : (
                <>
                  <p className="font-dashboard font-semibold text-brand-text">Arraste o arquivo aqui</p>
                  <p className="text-xs text-brand-text-muted font-body">ou clique para selecionar · .xlsx ou .xls</p>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="import-error">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-body">{error}</p>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="w-full py-3 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="upload-btn"
          >
            <Upload className="w-4 h-4" />
            {loading ? 'Importando...' : 'Importar Planilha'}
          </button>

          {/* Resultado */}
          {resultado && (
            <div className="card-dashboard space-y-4" data-testid="import-resultado">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-dashboard font-semibold text-brand-text">Importação Concluída</h3>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{resultado.inseridos}</p>
                  <p className="text-xs text-green-600 font-body">Inseridos</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{resultado.atualizados}</p>
                  <p className="text-xs text-blue-600 font-body">Atualizados</p>
                </div>
                <div className={`${resultado.erros?.length > 0 ? 'bg-red-50' : 'bg-gray-50'} rounded-lg p-3 text-center`}>
                  <p className={`text-2xl font-bold ${resultado.erros?.length > 0 ? 'text-red-700' : 'text-gray-500'}`}>{resultado.erros?.length || 0}</p>
                  <p className={`text-xs ${resultado.erros?.length > 0 ? 'text-red-600' : 'text-gray-500'} font-body`}>Erros</p>
                </div>
              </div>

              {resultado.erros?.length > 0 && (
                <div>
                  <p className="text-sm font-dashboard font-semibold text-red-700 mb-2">Linhas com erro:</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {resultado.erros.map((e, i) => (
                      <div key={i} className="flex gap-2 text-xs font-body">
                        <span className="text-brand-text-muted">Linha {e.linha}:</span>
                        <span className="text-red-600">{e.erro}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resultado.preview?.length > 0 && (
                <div>
                  <p className="text-sm font-dashboard font-semibold text-brand-text mb-2">Prévia:</p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-brand-border">
                    <table className="w-full text-xs font-body">
                      <thead className="bg-brand-bg sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-3 text-brand-text-muted">Linha</th>
                          <th className="text-left py-2 px-3 text-brand-text-muted">SKU</th>
                          <th className="text-left py-2 px-3 text-brand-text-muted">Produto</th>
                          <th className="text-left py-2 px-3 text-brand-text-muted">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultado.preview.map((r, i) => (
                          <tr key={i} className="border-t border-brand-border">
                            <td className="py-1.5 px-3 text-brand-text-muted">{r.linha}</td>
                            <td className="py-1.5 px-3 font-mono">{r.sku}</td>
                            <td className="py-1.5 px-3 text-brand-text">{r.descricao}</td>
                            <td className="py-1.5 px-3">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.acao === 'inserido' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                {r.acao}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Link to="/admin/produtos" className="flex-1 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm text-center" data-testid="ver-produtos-btn">
                  Ver Produtos
                </Link>
                <button onClick={() => { setResultado(null); setFile(null); }} className="px-4 py-2.5 rounded-lg border border-brand-border font-body text-sm">
                  Nova importação
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Template guide */}
        <div className="space-y-4">
          <div className="card-dashboard">
            <h3 className="font-dashboard font-semibold text-brand-text mb-3">Template de colunas</h3>
            <div className="space-y-1.5">
              {COLUNAS_TEMPLATE.map(c => (
                <div key={c.col} className="flex items-start gap-2">
                  <span className="w-6 h-5 rounded text-xs font-mono font-bold bg-brand-bg text-brand-text-muted flex items-center justify-center flex-shrink-0 mt-0.5">{c.col}</span>
                  <span className="text-xs font-body text-brand-text">{c.nome} {c.obrig && <span className="text-red-500">*</span>}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-brand-text-muted font-body mt-3">* Coluna obrigatória</p>
          </div>

          <button
            onClick={downloadTemplate}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-brand-border font-dashboard text-sm hover:bg-brand-bg transition-colors"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 text-brand-text-muted" />
            Baixar template CSV
          </button>

          <div className="card-dashboard bg-amber-50 border-amber-200">
            <p className="text-xs font-dashboard font-semibold text-amber-800 mb-1">Dicas</p>
            <ul className="text-xs text-amber-700 font-body space-y-1 list-disc list-inside">
              <li>SKU existente = produto atualizado</li>
              <li>SKU vazio = gerado automaticamente</li>
              <li>Preço vai para o tipo de cliente padrão</li>
              <li>Categorias são criadas automaticamente</li>
              <li className="font-semibold">Compatível com exports do ContaAzul, Omie, NFe.io</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
